import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { useAuth } from "../../../lib/auth-context.js";
import { listMessageThreads } from "../../../lib/api-modules/messages.js";
import { Pill } from "../../design-system/index.js";

/**
 * Phase 13.5 widget — unread spousal messages count + open-inbox CTA.
 */
export function MessagesWidget() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const threadsQuery = useQuery({
    queryKey: ["message-threads"] as const,
    queryFn: listMessageThreads,
    enabled: !!user,
    refetchInterval: 60_000,
  });
  const unread =
    threadsQuery.data?.threads.reduce(
      (sum, t) => sum + (t.unread_count ?? 0),
      0,
    ) ?? 0;

  return (
    <button
      type="button"
      onClick={() => void navigate("/messages")}
      data-testid="messages-nav"
      className="w-full rounded-xl border border-[var(--color-border-default)] bg-[var(--color-card)] p-5 text-left transition-colors hover:bg-[var(--color-elevated)]"
    >
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium uppercase tracking-wide text-[var(--color-muted)]">
          Messages
        </p>
        {unread > 0 ? (
          <Pill tone="accent">
            <span data-testid="messages-unread-badge">{unread} unread</span>
          </Pill>
        ) : (
          <Pill tone="neutral">All caught up</Pill>
        )}
      </div>
      <p className="mt-2 font-display text-xl font-medium text-[var(--color-text)]">
        {unread > 0
          ? unread === 1
            ? "1 new message"
            : `${unread} new messages`
          : "Inbox zero"}
      </p>
      <p className="mt-1 text-sm text-[var(--color-muted)]">
        Spousal threads pinned to entities — bills, accounts, decisions.
      </p>
    </button>
  );
}
